/**
 * Easy Pets — draggable, always-scrolling marquee.
 *
 * Shared by the brand bar and the reviews bar. Replaces the CSS animation with
 * a requestAnimationFrame loop so the track can ALSO be click/touch-dragged:
 *   - never pauses on hover (only while actively pressed/dragging)
 *   - drag left/right follows the pointer; release resumes at the same speed
 *   - a true single click still fires (links navigate); a drag suppresses it
 *   - an optional [data-marquee-toggle] button pauses/resumes the auto-scroll
 *     (WCAG 2.2.2 — a pause control for auto-moving content)
 *   - respects prefers-reduced-motion (no-op; CSS static fallback stays)
 *
 * PERF (2026-06): the rAF loop is gated by an IntersectionObserver so it only
 * runs while the marquee is on-screen and not paused — off-screen it fully
 * suspends, letting the main thread go idle (previously it spun forever, which
 * blocked Time-to-Interactive). The whole module is wrapped in an IIFE with an
 * idempotency guard so that loading this script from BOTH marquee sections on
 * one page is a harmless no-op instead of a duplicate-class SyntaxError.
 *
 * Markup: <easypets-draggable-marquee data-duration="32"> … [data-marquee-track] … </easypets-draggable-marquee>
 * `data-duration` = seconds for one set to pass (matches the old CSS duration).
 * The track must contain two identical sets of items (already rendered for the
 * seamless loop), so one set width = scrollWidth / 2.
 * The pause button may live anywhere inside the host section (it sits outside the
 * masked viewport so the edge-fade doesn't hide it).
 */
(function () {
  if (customElements.get('easypets-draggable-marquee')) return;

  class EasypetsDraggableMarquee extends HTMLElement {
    #track = null;
    #rafId = 0;
    #pos = 0;
    #setWidth = 0;
    #speed = 0; // px per second
    #duration = 40;
    #lastTs = 0;
    #dragging = false;
    #moved = false;
    #paused = false;
    #startX = 0;
    #startPos = 0;
    #resizeObserver = null;
    #intersectionObserver = null;
    #toggle = null;
    #visible = false;
    #running = false;

    connectedCallback() {
      this.#track = this.querySelector('[data-marquee-track]');
      if (!this.#track) return;

      // Honour reduced-motion: leave the CSS static fallback in place, do nothing.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.#duration = parseFloat(this.dataset.duration) || 40;

      // Take over from the CSS animation.
      this.#track.style.animation = 'none';
      this.#track.style.willChange = 'transform';

      this.#measure();
      this.#resizeObserver = new ResizeObserver(() => this.#measure());
      this.#resizeObserver.observe(this.#track);
      window.addEventListener('load', this.#measure);

      this.addEventListener('pointerdown', this.#onPointerDown);
      window.addEventListener('pointermove', this.#onPointerMove);
      window.addEventListener('pointerup', this.#onPointerUp);
      window.addEventListener('pointercancel', this.#onPointerUp);
      // Capture phase so we can stop a drag from triggering a link click.
      this.addEventListener('click', this.#onClickCapture, true);

      // Optional pause/play control. It lives outside the masked viewport (in the
      // host section), so look it up there too. Revealed only when JS drives the scroll.
      this.#toggle = this.querySelector('[data-marquee-toggle]') || this.closest('section')?.querySelector('[data-marquee-toggle]');
      if (this.#toggle) {
        this.#toggle.hidden = false;
        this.#toggle.addEventListener('click', this.#onToggle);
      }

      // Only animate while on-screen. Off-screen, the loop fully suspends so the
      // main thread can reach idle (fixes the perpetual-rAF / TTI problem).
      this.#intersectionObserver = new IntersectionObserver(this.#onIntersect, { threshold: 0 });
      this.#intersectionObserver.observe(this);
    }

    disconnectedCallback() {
      this.#stopLoop();
      this.#resizeObserver?.disconnect();
      this.#intersectionObserver?.disconnect();
      window.removeEventListener('load', this.#measure);
      window.removeEventListener('pointermove', this.#onPointerMove);
      window.removeEventListener('pointerup', this.#onPointerUp);
      window.removeEventListener('pointercancel', this.#onPointerUp);
      this.#toggle?.removeEventListener('click', this.#onToggle);
    }

    #onIntersect = (entries) => {
      this.#visible = entries[0].isIntersecting;
      if (this.#visible) {
        this.#startLoop();
      } else {
        this.#stopLoop();
      }
    };

    // Start the rAF loop unless it's already running, paused, or off-screen.
    #startLoop = () => {
      if (this.#running || this.#paused || !this.#visible) return;
      this.#running = true;
      this.#lastTs = performance.now();
      this.#rafId = requestAnimationFrame(this.#tick);
    };

    #stopLoop = () => {
      this.#running = false;
      cancelAnimationFrame(this.#rafId);
    };

    #measure = () => {
      // Track holds two identical sets; one set = half the scroll width.
      this.#setWidth = this.#track.scrollWidth / 2;
      this.#speed = this.#duration > 0 ? this.#setWidth / this.#duration : 0;
    };

    #tick = (ts) => {
      if (!this.#running) return; // suspended (off-screen or paused)
      const dt = Math.min(0.05, (ts - this.#lastTs) / 1000); // clamp (e.g. backgrounded tab)
      this.#lastTs = ts;
      if (!this.#dragging && !this.#paused && this.#setWidth > 0) {
        this.#pos -= this.#speed * dt;
        this.#normalize();
        this.#apply();
      }
      this.#rafId = requestAnimationFrame(this.#tick);
    };

    #normalize() {
      if (this.#setWidth <= 0) return;
      while (this.#pos <= -this.#setWidth) this.#pos += this.#setWidth;
      while (this.#pos > 0) this.#pos -= this.#setWidth;
    }

    #apply() {
      this.#track.style.transform = `translateX(${this.#pos}px)`;
    }

    /** @param {PointerEvent} event */
    #onPointerDown = (event) => {
      if (event.button != null && event.button > 0) return; // ignore right/middle click
      this.#dragging = true;
      this.#moved = false;
      this.#startX = event.clientX;
      this.#startPos = this.#pos;
      // NOTE: deliberately NOT using setPointerCapture — it retargets the
      // follow-up `click` to this element instead of the inner <a>, which breaks
      // single-click navigation on the brand links. Window-level pointermove/up
      // listeners already track the drag without capture.
      this.classList.add('is-dragging');
    };

    /** @param {PointerEvent} event */
    #onPointerMove = (event) => {
      if (!this.#dragging) return;
      const dx = event.clientX - this.#startX;
      if (Math.abs(dx) > 4) this.#moved = true;
      this.#pos = this.#startPos + dx;
      this.#normalize();
      this.#apply();
    };

    #onPointerUp = () => {
      if (!this.#dragging) return;
      this.#dragging = false;
      this.classList.remove('is-dragging');
      this.#lastTs = performance.now(); // prevent a dt jump on resume
    };

    /** @param {MouseEvent} event */
    #onClickCapture = (event) => {
      if (this.#moved) {
        event.preventDefault();
        event.stopPropagation();
        this.#moved = false;
      }
    };

    /** Pause/resume the auto-scroll from the toggle button. */
    #onToggle = () => {
      this.#paused = !this.#paused;
      this.#toggle.setAttribute('aria-pressed', String(this.#paused));
      this.#toggle.setAttribute('aria-label', this.#paused ? 'Play animation' : 'Pause animation');
      if (this.#paused) {
        this.#stopLoop();
      } else {
        this.#lastTs = performance.now(); // avoid a dt jump on resume
        this.#startLoop();
      }
    };
  }

  customElements.define('easypets-draggable-marquee', EasypetsDraggableMarquee);
})();
